'use client';
import React from 'react';
import Link from 'next/link';
import { Calendar, Clock, Heart, Bell, ArrowRight, CheckCircle, AlertCircle, Star } from 'lucide-react';

const stats = [
  { label: 'Upcoming Rituals', value: '2', icon: Calendar, color: 'text-saffron', bg: 'bg-saffron/10', href: '/dashboard/upcoming' },
  { label: 'Past Rituals', value: '8', icon: Clock, color: 'text-maroon', bg: 'bg-maroon/10', href: '/dashboard/past' },
  { label: 'Favourite Priests', value: '3', icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50', href: '/dashboard/favourites' },
  { label: 'Notifications', value: '3', icon: Bell, color: 'text-amber-500', bg: 'bg-amber-50', href: '/dashboard/notifications' },
];

const upcomingRituals = [
  {
    id: 'b001',
    ritual: 'Ganesh Puja',
    priest: 'Pandit Ramesh Kumar',
    date: 'Aug 7, 2026',
    time: '9:00 AM',
    status: 'confirmed',
    price: '₹2,500',
  },
  {
    id: 'b002',
    ritual: 'Satyanarayana Puja',
    priest: 'Pandit Suresh Sharma',
    date: 'Aug 15, 2026',
    time: '7:30 AM',
    status: 'pending',
    price: '₹3,200',
  },
];

const recentActivity = [
  { text: 'Booking confirmed for Ganesh Puja', time: '2 hours ago', type: 'success' },
  { text: 'Pandit Ramesh Kumar accepted your booking', time: '3 hours ago', type: 'info' },
  { text: 'Payment received for Navgraha Shanti', time: '2 days ago', type: 'success' },
];

export default function DashboardHome() {
  return (
    <div className="space-y-6 max-w-4xl">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-maroon to-deep-maroon rounded-2xl px-6 py-5 text-white relative overflow-hidden">
        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-5xl opacity-20 select-none">🪔</div>
        <p className="text-amber-200 text-sm font-medium mb-1">Namaste 🙏</p>
        <h2 className="font-display text-xl font-bold mb-1">Welcome back to your sacred space</h2>
        <p className="text-white/60 text-sm">Manage your rituals, priests, and bookings from here.</p>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats?.map((stat) => (
          <Link
            key={stat?.label}
            href={stat?.href}
            className="bg-white rounded-xl p-4 border border-gold/15 hover:border-gold/30 hover:shadow-md transition-all duration-200 group"
          >
            <div className={`w-9 h-9 rounded-lg ${stat?.bg} flex items-center justify-center mb-3`}>
              <stat.icon size={18} className={stat?.color} />
            </div>
            <p className="text-2xl font-bold text-maroon">{stat?.value}</p>
            <p className="text-xs text-maroon/50 mt-0.5 leading-snug">{stat?.label}</p>
          </Link>
        ))}
      </div>
      {/* Upcoming Rituals */}
      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold/10">
          <h3 className="font-semibold text-maroon text-sm">Upcoming Rituals</h3>
          <Link href="/dashboard/upcoming" className="text-xs text-saffron font-medium flex items-center gap-1 hover:underline">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        <div className="divide-y divide-gold/10">
          {upcomingRituals?.map((booking) => (
            <div key={booking?.id} className="px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-saffron/10 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🪔</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-maroon">{booking?.ritual}</p>
                <p className="text-xs text-maroon/50 mt-0.5">{booking?.priest} · {booking?.date} at {booking?.time}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-maroon">{booking?.price}</p>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  booking?.status === 'confirmed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                }`}>
                  {booking?.status === 'confirmed' ? '✓ Confirmed' : '⏳ Pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
        <div className="px-5 py-4 border-b border-gold/10">
          <h3 className="font-semibold text-maroon text-sm">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gold/10">
          {recentActivity?.map((item, i) => (
            <div key={i} className="px-5 py-3.5 flex items-start gap-3">
              {item?.type === 'success'
                ? <CheckCircle size={15} className="text-green-500 mt-0.5 flex-shrink-0" />
                : <AlertCircle size={15} className="text-saffron mt-0.5 flex-shrink-0" />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm text-maroon">{item?.text}</p>
                <p className="text-xs text-maroon/40 mt-0.5">{item?.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          href="/booking-flow"
          className="bg-saffron text-white rounded-xl px-4 py-3.5 flex items-center gap-3 hover:bg-saffron/90 transition-colors btn-glow"
        >
          <Calendar size={18} />
          <div>
            <p className="text-sm font-semibold">Book a Ritual</p>
            <p className="text-xs text-white/70">Schedule your next puja</p>
          </div>
        </Link>
        <Link
          href="/priests"
          className="bg-white border border-gold/20 rounded-xl px-4 py-3.5 flex items-center gap-3 hover:border-gold/40 hover:shadow-sm transition-all"
        >
          <Star size={18} className="text-gold" />
          <div>
            <p className="text-sm font-semibold text-maroon">Find a Priest</p>
            <p className="text-xs text-maroon/50">Browse verified priests</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
